import { Check, Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useUpdatePaymentSettings, usePaymentSettings } from '@/hooks/usePaymentSettings';
import { useUserContext } from '@/hooks/useUserContext';
import {
  newCorrelationId,
  type PaymentLinksTelemetryContext,
  PaymentLinksTelemetryEvents,
  trackPaymentLinksEvent,
} from '@/lib/payment-links-telemetry';
import { trackError } from '@/lib/telemetry';
import { usePaymentLinksEnabled } from '@/providers/FeatureFlagProvider';

const ONBOARDING_PATH = '/payments/onboarding';

// EDS `warning` palette (registry.eds.access-evo.com), declared locally because the shared
// `--accent-warning-*` tokens hold a different, pre-EDS amber.
const EDS_WARNING_TOKENS = [
  '[--eds-warning-surface:oklch(0.98_0.0229_88)] dark:[--eds-warning-surface:oklch(0.2_0.0513_52)]',
  '[--eds-warning-accent:oklch(0.77_0.125_83.2)] dark:[--eds-warning-accent:oklch(0.59_0.1215_76)]',
  '[--eds-warning-text:oklch(0.3_0.0708_58)] dark:[--eds-warning-text:oklch(0.95_0.05_86.8)]',
  '[--eds-warning-on-accent:oklch(1_0_0)]',
].join(' ');

const EDS_WARNING_SURFACE =
  'bg-[var(--eds-warning-surface)] border-[var(--eds-warning-accent)] text-[var(--eds-warning-text)]';

const TOOLTIP_BENEFITS = [
  'Customers pay in seconds from any device',
  'Payments reconcile automatically against invoices',
  'Reduce days sales outstanding and manual follow-up',
];

/**
 * Tenant-wide prompt shown to Collect administrators whose organisation has not completed PaySuite
 * merchant onboarding. The settings endpoint is admin-only and is queried only once the
 * `paymentLinks` flag is on and the user is an administrator.
 */
export function PaymentLinksOnboardingBanner() {
  const navigate = useNavigate();
  const isPaymentLinksEnabled = usePaymentLinksEnabled();
  const { isAdmin } = useIsAdmin();
  const { data: userContext } = useUserContext();
  const canReadSettings = isPaymentLinksEnabled && isAdmin;
  const { data: settings, isLoading, isError } = usePaymentSettings({ enabled: canReadSettings });
  const { mutate: updateSettings } = useUpdatePaymentSettings();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hasTrackedImpression = useRef(false);
  const hasTrackedFailure = useRef(false);
  const hasTrackedTooltip = useRef(false);
  const hasTrackedTooltipDismissal = useRef(false);

  const telemetryContext = useMemo<PaymentLinksTelemetryContext>(
    () => ({
      organisationId: userContext?.evoOrganisationId ?? undefined,
      companyId: userContext?.companyId?.toString(),
      role: userContext?.role ?? undefined,
      onboardingState: settings?.onboardingCompleted ? 'activated' : 'not_started',
    }),
    [userContext, settings],
  );

  const isDismissed = settings?.bannerDismissed ?? false;
  const isEligible = canReadSettings && !isLoading && !isError && settings?.onboardingCompleted === false;
  const isBannerVisible = isEligible && !isDismissed;

  useEffect(() => {
    if (!isBannerVisible || hasTrackedImpression.current) {
      return;
    }

    hasTrackedImpression.current = true;
    trackPaymentLinksEvent(PaymentLinksTelemetryEvents.BannerImpression, telemetryContext);
  }, [isBannerVisible, telemetryContext]);

  // Reported once per error episode, so a persistent failure does not flood telemetry on re-render.
  useEffect(() => {
    if (!isError) {
      hasTrackedFailure.current = false;
      return;
    }

    if (hasTrackedFailure.current) {
      return;
    }

    hasTrackedFailure.current = true;
    const correlationId = newCorrelationId();

    trackPaymentLinksEvent(PaymentLinksTelemetryEvents.StateResolutionFailed, telemetryContext, { correlationId });
    trackError(new Error(`Payment settings could not be resolved (${correlationId})`));
  }, [isError, telemetryContext]);

  // The tooltip also opens on hover, so only the first open/close pair is reported: tracking every
  // transition would emit telemetry at pointer rate without telling us anything new.
  const handleTooltipOpenChange = useCallback(
    (open: boolean) => {
      setIsTooltipOpen(open);

      if (open && !hasTrackedTooltip.current) {
        hasTrackedTooltip.current = true;
        trackPaymentLinksEvent(PaymentLinksTelemetryEvents.TooltipOpened, telemetryContext);
        return;
      }

      if (!open && hasTrackedTooltip.current && !hasTrackedTooltipDismissal.current) {
        hasTrackedTooltipDismissal.current = true;
        trackPaymentLinksEvent(PaymentLinksTelemetryEvents.TooltipDismissed, telemetryContext);
      }
    },
    [telemetryContext],
  );

  const handleOnboardingLink = () => {
    trackPaymentLinksEvent(PaymentLinksTelemetryEvents.OnboardingLinkSelected, telemetryContext);

    try {
      navigate(ONBOARDING_PATH);
    } catch (error) {
      const correlationId = newCorrelationId();

      trackPaymentLinksEvent(PaymentLinksTelemetryEvents.NavigationFailed, telemetryContext, { correlationId });
      trackError(
        error instanceof Error ? error : new Error(`Payment Links onboarding navigation failed (${correlationId})`),
      );
    }
  };

  const setDismissal = (dismissed: boolean) => {
    updateSettings(
      { bannerDismissed: dismissed },
      {
        // Reported only once the tenant preference is saved, so a failed write never looks applied.
        onSuccess: () => {
          trackPaymentLinksEvent(
            dismissed ? PaymentLinksTelemetryEvents.BannerDismissed : PaymentLinksTelemetryEvents.BannerRestored,
            telemetryContext,
          );
          trackPaymentLinksEvent(PaymentLinksTelemetryEvents.DismissalPreferenceUpdated, telemetryContext, {
            hidden: dismissed,
          });

          if (!dismissed) {
            hasTrackedImpression.current = false;
          }
        },
        onError: (error) => {
          const correlationId = newCorrelationId();

          trackPaymentLinksEvent(PaymentLinksTelemetryEvents.PreferenceUpdateFailed, telemetryContext, {
            hidden: dismissed,
            correlationId,
          });
          trackError(
            error instanceof Error
              ? error
              : new Error(`Payment Links banner preference update failed (${correlationId})`),
          );
        },
      },
    );
  };

  if (!isEligible) {
    return null;
  }

  if (isDismissed) {
    return (
      <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setDismissal(false)}>
        Show the Card Payment Links message for everyone in this organisation
      </Button>
    );
  }

  return (
    <Alert variant="warning" showIcon={false} className={`border-l-4 ${EDS_WARNING_TOKENS} ${EDS_WARNING_SURFACE}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-1">
            <AlertTitle className="mb-0">Card Payment Links are now available!</AlertTitle>
            <TooltipProvider delayDuration={100}>
              <Tooltip open={isTooltipOpen} onOpenChange={handleTooltipOpenChange}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="About Card Payment Links"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[var(--eds-warning-text)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <Info className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[320px] space-y-2">
                  <p className="font-medium">Get paid faster with Card Payment Links</p>
                  <p>
                    Send customers a secure payment link directly from Access Collect — they can pay outstanding
                    invoices instantly by card, no chasing required.
                  </p>
                  <ul className="space-y-1">
                    {TOOLTIP_BENEFITS.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="opacity-80">Complete your account setup to activate — it only takes a few minutes.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <AlertDescription className="mt-1">
            Let your customers pay invoices instantly by card. Complete your account setup to start sending payment
            links today.
          </AlertDescription>
        </div>

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            className="bg-[var(--eds-warning-accent)] text-[var(--eds-warning-on-accent)] hover:brightness-95"
            onClick={handleOnboardingLink}
          >
            Activate Card Payment Links
          </Button>
          <Button
            variant="link"
            size="sm"
            className="text-[var(--eds-warning-text)] underline"
            aria-label="Dismiss the Card Payment Links message for everyone in this organisation"
            onClick={() => setDismissal(true)}
          >
            Maybe later
          </Button>
        </div>
      </div>
    </Alert>
  );
}
